import {
  AlertCircle,
  Check,
  ChevronsUpDown,
  KeyRound,
  LoaderCircle,
  RefreshCw,
  Search,
  Sparkles,
} from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { filterModels, type ModelLoadState } from "../hooks/useModels";
import type {
  ModelInfo,
  Provider,
  ProviderStatus,
} from "../lib/types";

interface ModelSelectorProps {
  models: Record<Provider, ModelInfo[]>;
  provider: Provider;
  model: string;
  states?: Record<Provider, ModelLoadState>;
  statuses?: Partial<Record<"openrouter" | "gemini", ProviderStatus>>;
  onRefresh?: (provider: Provider) => Promise<void>;
  onChange: (provider: Provider, model: string) => void;
}

export function ModelSelector({
  models,
  provider,
  model,
  states,
  statuses,
  onRefresh,
  onChange,
}: ModelSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Provider>(provider);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const current = models[provider].find((item) => item.id === model);
  const unavailable = Boolean(model && !current);
  const filtered = useMemo(
    () => filterModels(models[filter], query),
    [filter, models, query],
  );

  useEffect(() => {
    setActiveIndex(0);
  }, [filter, query]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  const choose = (item: ModelInfo) => {
    onChange(filter, item.id);
    setOpen(false);
    setQuery("");
  };

  const state = states?.[filter];
  const credential =
    filter === "universal" ? undefined : statuses?.[filter];

  return (
    <div className="model-selector" ref={rootRef}>
      <button
        className={`model-selector-trigger ${unavailable ? "invalid" : ""}`}
        onClick={() => {
          setOpen((value) => !value);
          setFilter(provider);
          queueMicrotask(() => inputRef.current?.focus());
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
      >
        <span className={`provider-glyph ${provider}`}>
          <Sparkles size={15} />
        </span>
        <span>
          <small>{providerLabel(provider)}</small>
          <strong>
            {current?.name ??
              (model ? `${model} · non disponibile` : "Seleziona un modello")}
          </strong>
        </span>
        <ChevronsUpDown size={15} />
      </button>
      {open && (
        <div className="model-popover">
          <div className="model-provider-tabs" role="tablist">
            {(["openrouter", "gemini", "universal"] as Provider[]).map(
              (item) => (
                <button
                  key={item}
                  role="tab"
                  aria-selected={filter === item}
                  className={filter === item ? "active" : ""}
                  onClick={() => setFilter(item)}
                >
                  {providerLabel(item)}
                </button>
              ),
            )}
          </div>
          <label className="popover-search">
            <Search size={15} />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                  event.preventDefault();
                  setActiveIndex((index) =>
                    Math.min(index + 1, Math.max(0, filtered.length - 1)),
                  );
                } else if (event.key === "ArrowUp") {
                  event.preventDefault();
                  setActiveIndex((index) => Math.max(0, index - 1));
                } else if (event.key === "Enter" && filtered[activeIndex]) {
                  event.preventDefault();
                  choose(filtered[activeIndex]);
                } else if (event.key === "Escape") {
                  event.preventDefault();
                  setOpen(false);
                }
              }}
              placeholder={`Cerca in ${providerLabel(filter)}`}
              role="combobox"
              aria-autocomplete="list"
              aria-controls={listId}
              aria-activedescendant={
                filtered[activeIndex]
                  ? `${listId}-${activeIndex}`
                  : undefined
              }
            />
            {onRefresh && (
              <button
                className="model-refresh"
                title="Aggiorna modelli"
                onClick={() => void onRefresh(filter)}
              >
                <RefreshCw
                  size={14}
                  className={state?.kind === "loading" ? "spin" : ""}
                />
              </button>
            )}
          </label>
          <ModelListStatus
            state={state}
            credential={credential}
            query={query}
            count={filtered.length}
          />
          <div className="model-scroll" id={listId} role="listbox">
            {filtered.map((item, index) => (
              <button
                id={`${listId}-${index}`}
                role="option"
                aria-selected={filter === provider && item.id === model}
                className={`model-option ${index === activeIndex ? "active" : ""}`}
                key={`${filter}:${item.id}`}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(item)}
              >
                <span>
                  <strong>{item.name}</strong>
                  <em>{item.id}</em>
                  <small>
                    {formatContext(item.contextWindow)}
                    {item.inputPrice !== undefined
                      ? ` · $${item.inputPrice.toFixed(2)}/M input`
                      : " · prezzo n/d"}
                  </small>
                </span>
                {filter === provider && item.id === model && <Check size={16} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ModelListStatus({
  state,
  credential,
  query,
  count,
}: {
  state: ModelLoadState | undefined;
  credential: ProviderStatus | undefined;
  query: string;
  count: number;
}) {
  if (state?.kind === "loading") {
    return (
      <div className="model-list-state loading">
        <LoaderCircle className="spin" size={13} /> Caricamento modelli…
      </div>
    );
  }
  if (credential && !credential.hasApiKey) {
    return (
      <div className="model-list-state error">
        <KeyRound size={13} /> Chiave mancante. Apri Impostazioni → Provider AI.
      </div>
    );
  }
  if (state && state.kind !== "ready" && state.kind !== "idle") {
    return (
      <div className="model-list-state error">
        <AlertCircle size={13} /> {state.message ?? stateLabel(state.kind)}
      </div>
    );
  }
  if (!count) {
    return (
      <div className="model-list-state empty">
        {query ? "Nessun risultato per la ricerca." : "Lista modelli vuota."}
      </div>
    );
  }
  return (
    <div className="model-list-state ready">
      {count} {count === 1 ? "modello disponibile" : "modelli disponibili"}
    </div>
  );
}

function stateLabel(kind: ModelLoadState["kind"]): string {
  switch (kind) {
    case "missing_key":
      return "Chiave mancante.";
    case "invalid_key":
      return "Chiave non valida.";
    case "network_error":
      return "Errore di rete.";
    case "provider_error":
      return "Errore del provider.";
    default:
      return "Lista modelli non disponibile.";
  }
}

export function providerLabel(provider: Provider): string {
  if (provider === "openrouter") return "OpenRouter";
  if (provider === "gemini") return "Google AI Studio";
  return "Universal Endpoint";
}

export function formatContext(tokens: number): string {
  if (!tokens) return "context n/d";
  if (tokens >= 1_000_000) {
    return `${(tokens / 1_000_000).toFixed(tokens % 1_000_000 ? 1 : 0)}M context`;
  }
  return `${Math.round(tokens / 1000)}K context`;
}
